import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';

import i18n, { translations } from 'i18n';
import { isiOS } from 'config';

import { addFavorite, removeFavorite } from 'actions/registry';

import { uninstallApp } from 'actions/gallery';

import Layout from 'components/Layout';
import ImageContainer from 'components/ImageContainer';
import LinkRow from 'components/LinkRow';
import {
  RatingTransition,
  ConnectedRatingInput
} from 'components/Ratings/RatingInput';

import Modal from './Modal';

import InstallButtonWithPermission from 'components/InstallButtonWithPermission';
import { roundEdgeDevices } from '../../../constants';

const DEVICES = {
  ionic: {
    sizeMulti: 0.488
  },
  versa: {
    sizeMulti: 0.5
  },
  'versa lite': {
    sizeMulti: 0.5
  },
  'versa 2': {
    sizeMulti: 0.5
  },
  'charge 3': {
    sizeMulti: 1
  },
  'charge 4': {
    sizeMulti: 1
  },
  inspire: {
    sizeMulti: 1
  },
  'versa 3': {
    sizeMulti: 0.7
  },
  'versa 4': {
    sizeMulti: 0.7
  },
  sense: {
    sizeMulti: 0.7
  },
  'sense 2': {
    sizeMulti: 0.7
  }
};

const getDimensions = ({
  image: { width, height },
  device: { sizeMulti = 1 } = {}
}) => ({
  width: width * sizeMulti,
  height: height * sizeMulti
});

const TransitionWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  overflow: hidden;
`;

const MainContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  height: 100%;
  width: 100%;

  background-color: #ffffff;

  z-index: 65536;

  transition: all 0.2s ease-in-out;

  ${({ show }) =>
    show
      ? css`
          top: 0;
        `
      : ''}
`;

const DoneText = styled.span`
  margin-right: 20px;

  font-weight: bold;
`;

const Done = ({ setFocusedClock }) => (
  <DoneText onClick={() => setFocusedClock(null)}>
    {i18n('page.ClockGallery.MyClocks.details.doneButton')()}
  </DoneText>
);

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  text-align: left;

  overflow-y: auto;

  background-color: #ffffff;
`;

const DetailsContainer = styled.div`
  margin-top: auto;
  margin-bottom: auto;
  padding-top: 20px;
  padding-bottom: 10px;
`;

const ClockImageContainer = styled.div`
  position: relative;

  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;

  ${({ width }) => css`
    width: ${width}px;
  `}

  &:before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    top: -10px;
    left: -10px;
    ${({ width, height }) => css`
      width: ${width + 20}px;
      height: ${height + 20}px;
    `}
    background-color: #ffffff;
    ${({ borderUrl, deviceName }) => {
      if (roundEdgeDevices.includes(deviceName)) {
        return `background-image: url(${borderUrl});`;
      }

      return 'border: 3px solid #cfd3d6; border-radius: 15px;';
    }}
`;

const ClockImage = styled(({ image, name, ...props }) => (
  <ImageContainer
    {...props}
    containerAttr={{
      width: image.width,
      height: image.height
    }}
    imageAttr={{
      alt: name,
      src: image.uri
    }}
  />
))`
  position: relative;

  ${({ width, height }) => css`
    width: ${width}px;
    height: ${height}px;
  `}

  overflow: hidden;
  border-radius: 6px;
`;

const AppName = styled.div`
  margin-bottom: 5px;

  text-align: center;

  font-size: 24px;
  font-weight: 500;
  color: #112530;
`;

const DevName = styled.div`
  margin-bottom: 20px;

  text-align: center;

  font-size: 15px;
  color: #a0a8ac;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const SelectButton = (props) => {
  const {
    page: {
      Clock: { InstallButtonState }
    }
  } = translations;

  const installTexts = Object.keys(InstallButtonState).reduce((texts, key) => {
    texts[key] = InstallButtonState[key]();
    return texts;
  }, {});

  return (
    <ButtonContainer>
      <InstallButtonWithPermission texts={installTexts} {...props} />
    </ButtonContainer>
  );
};

const ActionsContainer = styled.div`
  ${({ theme }) => theme.AppMainContent};
`;

const ManageText = styled.span`
  color: #${({ add, loading }) => (loading ? '9b9b9b' : add ? '00b0b9' : 'f0416f')};
`;

class ManageFavoriteButton extends Component {
  state = {
    open: false
  };

  onOpen = () =>
    this.setState({
      open: true
    });

  onClose = () =>
    this.setState({
      open: false
    });

  onAddClick = () => {
    const {
      clock,
      favoriteClocksCount,
      maxFavoriteClocks,
      addFavorite
    } = this.props;

    if (favoriteClocksCount >= maxFavoriteClocks) {
      this.onOpen();
      return;
    }

    addFavorite(clock.id);
  };

  onRemoveClick = () => {
    const { clock, removeFavorite } = this.props;

    if (clock.selected) {
      removeFavorite(clock.id);
    } else {
      this.onOpen();
    }
  };

  onButtonClick = () =>
    this.props.clock.favorite ? this.onRemoveClick() : this.onAddClick();

  render() {
    const { clock, addLoading, removeLoading, removeFavorite } = this.props;

    const {
      page: {
        ClockGallery: {
          MyClocks: { details }
        }
      }
    } = translations;

    let mainButtonText = addLoading ? details.addLoading : details.addButton;
    let modalText = details.limitModal;
    let buttons = [];

    if (clock.selected && clock.favorite) {
      mainButtonText = removeLoading
        ? details.removeLoading
        : details.removeFavoriteButton;
    } else if (clock.favorite) {
      mainButtonText = removeLoading
        ? details.removeLoading
        : details.removeButton;
      modalText = details.removeModal;

      buttons = [
        {
          text: modalText.confirmButton(),
          highlight: true,
          onClick: () => {
            removeFavorite(clock.id);
            this.onClose();
          }
        }
      ];
    }

    buttons = [
      {
        text: modalText.button(),
        onClick: this.onClose
      },
      ...buttons
    ];

    return (
      <div>
        <LinkRow
          showArrow={false}
          text={
            <ManageText
              add={!clock.favorite}
              loading={addLoading || removeLoading}
            >
              {mainButtonText()}
            </ManageText>
          }
          onClick={this.onButtonClick}
        />
        {this.state.open && (
          <Modal
            title={modalText.title()}
            content={modalText.content()}
            buttons={buttons}
            onClose={this.onClose}
          />
        )}
      </div>
    );
  }
}

const Details = ({
  clock: {
    banners: [image],
    name,
    developer
  },
  clock,
  device,
  deviceName
}) => (
  <DetailsContainer>
    <ClockImageContainer
      {...getDimensions({ image, device })}
      borderUrl="/device-images/squircleDeviceBorder.svg"
      deviceName={deviceName}
    >
      <ClockImage image={image} {...getDimensions({ image, device })} />
    </ClockImageContainer>
    <AppName>{name}</AppName>
    <DevName>{developer.name}</DevName>
    <SelectButton app={clock} />
  </DetailsContainer>
);

const RatingWrapper = styled.div`
  ${({ theme }) => css`
    padding: 8px 0;
    background-color: ${theme.backgroundColor};

    ${isiOS
      ? css`
          border-top: 1px solid ${theme.borderColor};
        `
      : ''}
  `}
`;

const RatingContainer = styled.div`
  ${({ theme }) => css`
    padding: 20px ${theme.edgeDistance};
    background-color: #ffffff;

    ${isiOS
      ? css`
          border-top: 1px solid ${theme.borderColor};
          border-bottom: 1px solid ${theme.borderColor};
        `
      : ''}
  `}
`;

const RatingTitle = styled.div`
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.fontBold};
  font-size: 17px;
  text-align: center;
  color: #112530;
`;

const Rating = ({ clock }) => (
  <RatingTransition show={clock.showRatingInput}>
    <RatingWrapper>
      <RatingContainer>
        <RatingTitle>
          {i18n('component.Ratings.rateThisItemText')()}
        </RatingTitle>
        <ConnectedRatingInput app={clock} />
      </RatingContainer>
    </RatingWrapper>
  </RatingTransition>
);

const ClockDetailsView = ({
  show,
  clock,
  deviceName,
  favoriteClocksEnabled,
  favoriteClocksCount,
  maxFavoriteClocks,
  addLoading,
  removeLoading,
  addFavorite,
  removeFavorite,
  setFocusedClock,
  isFirstClock
}) => (
  <TransitionWrapper>
    <MainContainer show={show}>
      <Layout
        right={<Done setFocusedClock={setFocusedClock} />}
        name="Gallery"
        pageElement={
          !!clock && (
            <BodyContainer>
              <Details
                clock={clock}
                device={DEVICES[deviceName]}
                deviceName={deviceName}
              />
              <Rating clock={clock} />
              <ActionsContainer>
                {clock.isInstalled && clock.hasSettings && (
                  <LinkRow
                    text={i18n('page.ClockGallery.MyClocks.details.settings')()}
                    onClick={() => clock.navigateToSettings()}
                  />
                )}
                <LinkRow
                  to={`/clock/${clock.id}`}
                  showArrow={false}
                  text={i18n(
                    'page.ClockGallery.MyClocks.details.appDetailsButton'
                  )()}
                />
                {favoriteClocksEnabled &&
                  !clock.isInstalling &&
                  !(clock.selected || isFirstClock) && (
                    <ManageFavoriteButton
                      clock={clock}
                      favoriteClocksCount={favoriteClocksCount}
                      maxFavoriteClocks={maxFavoriteClocks}
                      addLoading={addLoading}
                      removeLoading={removeLoading}
                      addFavorite={addFavorite}
                      removeFavorite={removeFavorite}
                    />
                  )}
              </ActionsContainer>
            </BodyContainer>
          )
        }
      />
    </MainContainer>
  </TransitionWrapper>
);

const mapStateToProps = ({ gallery, registry }) => ({
  addLoading: registry.addFavorite.loading,
  removeLoading: gallery.uninstallApp.loading || registry.removeFavorite.loading
});

const mapDispatchToProps = (dispatch, { clock }) => ({
  addFavorite: () => dispatch(addFavorite(clock.id)),
  removeFavorite: () => {
    if (!clock.favorite) {
      return;
    }

    if (!clock.selected && clock.isOnDevice) {
      return dispatch(uninstallApp(clock.id));
    }

    dispatch(removeFavorite(clock.id));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ClockDetailsView);
